/**
 * Starter templates for the +New Insight menu.
 *
 * Each template describes a pre-populated dashboard/report layout that
 * the builder hydrates on mount when navigated with `?template=<id>`.
 *
 * Widget `vizType` values match the Report builder's internal names
 * (`kpi`, `bar_chart`, `line_chart`, `pie_chart`, `area_chart`, `table`)
 * so hydration produces a valid initial state.
 */
export const INSIGHT_TEMPLATES = [
  {
    id: 'executive_overview',
    name: 'Executive Overview',
    description: 'Four headline KPIs, a trend line, and a breakdown by category — the classic C-suite one-pager.',
    icon: 'LineChart',
    color: '#3b82f6',
    layout: {
      widgets: [
        { vizType: 'kpi',        title: 'Total Revenue',     x: 0, y: 0, w: 3, h: 2 },
        { vizType: 'kpi',        title: 'Active Customers',  x: 3, y: 0, w: 3, h: 2 },
        { vizType: 'kpi',        title: 'Avg. Order Value',  x: 6, y: 0, w: 3, h: 2 },
        { vizType: 'kpi',        title: 'YoY Growth',        x: 9, y: 0, w: 3, h: 2 },
        { vizType: 'line_chart', title: 'Revenue Trend',     x: 0, y: 2, w: 8, h: 4 },
        { vizType: 'bar_chart',  title: 'Top Categories',    x: 8, y: 2, w: 4, h: 4 },
      ],
    },
  },
  {
    id: 'kpi_grid',
    name: 'KPI Grid',
    description: 'A 4x2 grid of KPI cards for at-a-glance monitoring — great for a wall-mounted status board.',
    icon: 'LayoutGrid',
    color: '#10b981',
    layout: {
      widgets: Array.from({ length: 8 }, (_, i) => ({
        vizType: 'kpi',
        title: `KPI ${i + 1}`,
        x: (i % 4) * 3,
        y: Math.floor(i / 4) * 2,
        w: 3, h: 2,
      })),
    },
  },
  {
    id: 'trend_deep_dive',
    name: 'Trend Deep Dive',
    description: 'One full-width line chart with year-over-year overlay, plus a compare-mode table underneath.',
    icon: 'TrendingUp',
    color: '#8b5cf6',
    layout: {
      widgets: [
        { vizType: 'line_chart', title: 'YoY Trend',           x: 0, y: 0, w: 12, h: 5 },
        { vizType: 'table',      title: 'Period-over-Period',  x: 0, y: 5, w: 12, h: 4 },
      ],
    },
  },
  {
    id: 'breakdown_analysis',
    name: 'Breakdown Analysis',
    description: 'Pie + bar + top-N table on the same dimension — quickly see composition and rankings.',
    icon: 'PieChart',
    color: '#f59e0b',
    layout: {
      widgets: [
        { vizType: 'pie_chart', title: 'Composition',   x: 0, y: 0, w: 4, h: 4 },
        { vizType: 'bar_chart', title: 'Ranked',        x: 4, y: 0, w: 8, h: 4 },
        { vizType: 'table',     title: 'Top 10',        x: 0, y: 4, w: 12, h: 4 },
      ],
    },
  },
];

export function getTemplateById(id) {
  if (!id) return null;
  return INSIGHT_TEMPLATES.find(t => t.id === id) || null;
}

/**
 * Convert a template definition into the concrete builder state:
 *   - `visuals`: array of visual objects the builder expects
 *   - `gridLayouts`: react-grid-layout `{ lg: [...] }` shape
 * Also returns a suggested `reportName`.
 */
export function buildStateFromTemplate(template) {
  if (!template || !template.layout || !Array.isArray(template.layout.widgets)) {
    return null;
  }
  const widgets = template.layout.widgets;
  const now = Date.now();
  const visuals = widgets.map((w, i) => ({
    id: `t${now}_${i}`,
    vizType: w.vizType || 'bar_chart',
    title: w.title || `Visual ${i + 1}`,
    rowDims: [],
    colDim: '',
    valueMeasures: [],
    filterDims: [],
    _fromTemplate: template.id,
  }));
  const lg = widgets.map((w, i) => ({
    i: visuals[i].id,
    x: Number.isFinite(w.x) ? w.x : (i % 2) * 6,
    y: Number.isFinite(w.y) ? w.y : Math.floor(i / 2) * 4,
    w: Number.isFinite(w.w) ? w.w : 6,
    h: Number.isFinite(w.h) ? w.h : 4,
    minW: 2,
    minH: 1,
  }));
  return {
    reportName: template.name,
    visuals,
    gridLayouts: { lg },
  };
}
